import React from "react";
import { LegalLayout, Section } from "@/components/legal/LegalLayout";
import { Sparkles, ScanEye, MessageSquare, TrendingUp } from "lucide-react";

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[color:var(--sg-fg)] leading-relaxed">{children}</p>
);

export const AboutPage: React.FC = () => (
  <LegalLayout
    testId="page-about"
    title="About SociBaba"
    subtitle="Capture social signals. Turn them into action."
    lastUpdated="February 13, 2026"
    seoPath="/about"
    seoDescription="SociBaba is a social intelligence workspace for creators, influencers, D2C brands and SMBs — publish, discover market demand, and turn comments into leads."
    seoKeywords={["about SociBaba","social intelligence workspace","creator platform","brand social CRM"]}
  >
    <P>
      SociBaba is a social intelligence workspace built for creators, influencers, D2C brands, agencies, and Indian SMB owners. We help you find what the market is already asking for, publish content across your channels, and turn every comment into a lead, a fix, or a decision.
    </P>

    <Section n={1} title="What we do">
      <ul className="space-y-3 mt-2 list-none pl-0">
        <li className="flex items-start gap-3">
          <ScanEye className="h-4 w-4 mt-1 text-[color:var(--sg-violet)] shrink-0" />
          <span><b>Discovery</b> — scan public conversations across Reddit, X, YouTube, Quora and more to surface demand, pain points, buying intent and content opportunities.</span>
        </li>
        <li className="flex items-start gap-3">
          <Sparkles className="h-4 w-4 mt-1 text-[color:var(--sg-primary)] shrink-0" />
          <span><b>Publishing</b> — one workspace to publish product/content across YouTube, Facebook Pages and Instagram Business, with automatic thumbnail generation and media lifecycle.</span>
        </li>
        <li className="flex items-start gap-3">
          <MessageSquare className="h-4 w-4 mt-1 text-emerald-600 shrink-0" />
          <span><b>Comment intelligence</b> — AI-classified comments (buying intent, product question, complaint, neutral, spam), with grounded reply drafts you approve with one click.</span>
        </li>
        <li className="flex items-start gap-3">
          <TrendingUp className="h-4 w-4 mt-1 text-amber-600 shrink-0" />
          <span><b>Insights & opportunity scoring</b> — see which products/content are top performers, where hidden winners are, and where to double-down.</span>
        </li>
      </ul>
    </Section>

    <Section n={2} title="Our principles">
      <ul className="list-disc ml-6 space-y-2">
        <li><b>Grounded AI</b> — reply drafts and Discovery reports are grounded in your own product data and public excerpts. We never invent facts.</li>
        <li><b>Public-content only</b> — Discovery only summarises publicly-accessible discussions. No scraping of private data.</li>
        <li><b>Your data, your control</b> — full export any time; deletion in 30 days on request.</li>
        <li><b>Lightweight & fast</b> — a premium workspace that stays out of your way.</li>
      </ul>
    </Section>

    <Section n={3} title="Contact">
      <P>Say hi at <a className="text-[color:var(--sg-primary)] hover:underline" href="mailto:hello@socibaba.com">hello@socibaba.com</a>.</P>
    </Section>
  </LegalLayout>
);

export default AboutPage;
